import React, { useEffect, useState } from "react";
import { useAccount, useToken, useBalance, useReadContract, useBlockNumber } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import style from "./redeem.module.css";
import WETHX_PTN_POOL_ABI from "../../../Config/WETHX_PTN_POOL_ABI.json";
import WETHX_PTN_MASTER_ORACLE_ABI from "../../../Config/WETHX_PTN_MASTER_ORACLE_ABI.json";
import {
  WETHX_PTN_POOL_ADDRESS,
  WETH_TOKEN_ADDRESS,
  WETHX_PTN_MASTER_ORACLE_ADDRESS,
  GUARDX_POOL_ADDRESS,
  GUARD_TOKEN_ADDRESS,
  GUARDX_MASTER_ORACLE_ADDRESS,
  ANDYX,
  ANDYX_POOL_ADDRESS,
  ANDY_TOKEN_ADDRESS,
  ANDYX_MASTER_ORACLE_ADDRESS
} from "../../../Config";
import useCustomContractWrite from "../../../Hooks/useCustomContractWrite";
import useCustomContractRead from "../../../Hooks/useCustomContractRead";
import convertWeiToEther from "../../../Utils/convertWeiToEther";
import ApproveButton from "../../common/ApproveButton";
import Loader from "../../Loader/Loader";
import convertEtherToWei from "../../../Utils/convertEtherToWei";
import useCheckAllowance from "../../../Hooks/useCheckAllowance";
import { ethers } from "ethers";
import { NewFooter } from "../../NewFooter";

const Redeem = () => {
  // Token selection state
  const [selectedToken, setSelectedToken] = useState("BNBX");
  const [showTokenDropdown, setShowTokenDropdown] = useState(false);

  // Token configurations
  const TOKEN_CONFIG = {
    BNBX: {
      poolAddress: WETHX_PTN_POOL_ADDRESS,
      tokenAddress: "",
      xTokenSymbol: "BNBX",
      outputTokenSymbol: "BNB",
      shieldSymbol: "SHiELD",
      icon: "/icons/bnb.png",
      shieldIcon: "/icons/logo.svg",
      masterOracle: WETHX_PTN_MASTER_ORACLE_ADDRESS
    },
    GUARDX: {
      poolAddress: GUARDX_POOL_ADDRESS,
      tokenAddress: GUARD_TOKEN_ADDRESS,
      xTokenSymbol: "GUARDX",
      outputTokenSymbol: "GUARD",
      shieldSymbol: "SHiELD",
      icon: "/icons/guard.png",
      shieldIcon: "/icons/logo.svg",
      masterOracle: GUARDX_MASTER_ORACLE_ADDRESS
    },
    ANDYX: {
      poolAddress: ANDYX_POOL_ADDRESS,
      tokenAddress: ANDY_TOKEN_ADDRESS,
      xTokenSymbol: "ANDYX",
      outputTokenSymbol: "ANDY",
      shieldSymbol: "SHiELD",
      icon: "/icons/andy.png",
      shieldIcon: "/icons/logo.svg",
      masterOracle: ANDYX_MASTER_ORACLE_ADDRESS
    }
  };

  const currentConfig = TOKEN_CONFIG[selectedToken];

  // Input states
  const [alienInputValue, setAlienInputValue] = useState("");
  const [alienXInputValue, setAlienXInputValue] = useState("");
  const [shieldInputValue, setShieldInputValue] = useState("");
  const [SlippageInPercent, setSlippageInPercent] = useState(0.5);
  const [isAproveERC20, setIsApprovedERC20] = useState(true);

  // Wagmi hooks
  const { address, isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { data: currentBlock } = useBlockNumber({ watch: true });

  // Contract reads
  const { data: calcRedeem } = useCustomContractRead({
    Adrress: currentConfig.poolAddress,
    Abi: WETHX_PTN_POOL_ABI,
    FuncName: "calcRedeem",
    Args: [convertEtherToWei?.(alienXInputValue === "" ? "0" : alienXInputValue)],
    isEnabled: alienXInputValue,
    onSuccess: (data) => {
      setAlienInputValue(
        parseFloat?.(convertWeiToEther?.(data?.[0]?.toString())).toFixed(4)
      );
      setShieldInputValue(
        parseFloat?.(convertWeiToEther?.(data?.[1]?.toString())).toFixed(4)
      );
    },
  });

  const { data: poolInfo } = useCustomContractRead({
    Adrress: currentConfig.poolAddress,
    Abi: WETHX_PTN_POOL_ABI,
    FuncName: "info",
  });

  const { data: xTokenAddress } = useCustomContractRead({
    Adrress: currentConfig.poolAddress,
    Abi: WETHX_PTN_POOL_ABI,
    FuncName: "xToken",
  });

  const { data: yTokenAddress } = useCustomContractRead({
    Adrress: currentConfig.poolAddress,
    Abi: WETHX_PTN_POOL_ABI,
    FuncName: "yToken",
  });

  // Token info hooks
  const { data: alienXInfo } = useToken({
    address: xTokenAddress,
    enabled: xTokenAddress !== undefined,
  });

  const { data: alienYInfo } = useToken({
    address: yTokenAddress,
    enabled: yTokenAddress !== undefined,
  });

  // Balance checks
  const { data: alienBalance } = useBalance({
    address: address,
    token: currentConfig.tokenAddress,
    watch: true,
    enabled: address !== undefined,
  });

  const { data: alienXBalance } = useBalance({
    address: address,
    token: xTokenAddress,
    watch: true,
    enabled: address !== undefined,
  });

  const { data: shieldBalance } = useBalance({
    address: address,
    token: yTokenAddress,
    watch: true,
    enabled: address !== undefined,
  });

  // Allowance check
  const { data: checkAllowance } = useCheckAllowance({
    tokenAddress: xTokenAddress,
    spenderAddress: currentConfig.poolAddress,
    isLpToken: false,
  });

  // User info
  const { data: userInfo, refetch: refetchUserInfo } = useReadContract({
    address: currentConfig.poolAddress,
    abi: WETHX_PTN_POOL_ABI,
    functionName: "userInfo",
    args: [address],
    enabled: address !== undefined,
    watch: true 
  });

  // Effects
  useEffect(() => {
    if (address) {
      const price = parseFloat?.(alienXInputValue === "" ? "1" : alienXInputValue);
      const allowance = parseFloat?.(convertWeiToEther?.(checkAllowance));
      setIsApprovedERC20(allowance >= price);
    }
  }, [checkAllowance, address, alienXInputValue]);

  useEffect(() => {
    refetchUserInfo();
  }, [currentBlock]);

  // Handlers
  const handleAlienXInputChange = (event, redeemingFee) => {
    const { value } = event.target;
    const isStack = /^[0-9]*(\.[0-9]{0,9})?$/.test(value);
    if (isStack) {
      setAlienXInputValue(value);
      const valueAfterFeeCut = value !== ""
        ? (parseFloat?.(value) - parseFloat?.(value) * (redeemingFee / 100)).toFixed(0)
        : "0.0";
      setAlienInputValue(valueAfterFeeCut);
    }
  };

  const setMax = () => {
    if (alienXBalance) {
      const balance = ethers.utils.parseUnits(alienXBalance.formatted, alienXBalance.decimals);
      const formattedValue = ethers.utils.formatUnits(balance, alienXBalance.decimals);
      setAlienXInputValue(formattedValue);
    }
  };

  const handleTokenSelect = (token) => {
    setSelectedToken(token);
    setShowTokenDropdown(false);
    setAlienInputValue("");
    setAlienXInputValue("");
    setShieldInputValue("");
  };

  // Contract writes
  const {
    executeWrite: executeWriteRedeem,
    isLoading: isLoadingRedeem,
  } = useCustomContractWrite({
    address: currentConfig.poolAddress,
    abi: WETHX_PTN_POOL_ABI,
    functionName: "redeem",
    args: [
      convertEtherToWei?.(alienXInputValue === "" ? "0" : alienXInputValue),
      convertEtherToWei?.(
        shieldInputValue !== ""
          ? (parseFloat?.(shieldInputValue) - parseFloat?.(shieldInputValue) * (SlippageInPercent / 100)).toString()
          : "0.0"
      ),
      convertEtherToWei?.(
        alienInputValue !== ""
          ? (parseFloat?.(alienInputValue) - parseFloat?.(alienInputValue) * (SlippageInPercent / 100)).toString()
          : "0.0"
      ),
    ],
    isEnabled:
      parseFloat?.(alienXInputValue === "" ? "0" : alienXInputValue) > 0 &&
      parseFloat?.(alienXInputValue === "" ? "0" : alienXInputValue) <=
      parseFloat?.(alienXBalance?.formatted) &&
      address !== undefined &&
      !poolInfo?.[5] &&
      isAproveERC20,
  });

  const {
    executeWrite: executeWriteCollect,
    isLoading: isLoadingCollect,
  } = useCustomContractWrite({
    address: currentConfig.poolAddress,
    abi: WETHX_PTN_POOL_ABI,
    functionName: "collect",
  });

  const handleRedeem = async () => {
    try {
      await executeWriteRedeem();
      refetchUserInfo();
      setAlienXInputValue("");
    } catch (error) {
      console.error("Redeem error:", error);
    }
  };

  // Price oracle
  const { data: shieldPriceRate } = useCustomContractRead({
    Adrress: currentConfig.masterOracle,
    Abi: WETHX_PTN_MASTER_ORACLE_ABI,
    FuncName: "getYTokenPrice",
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showTokenDropdown && !e.target.closest(`.${style.tokenDropdownContainer}`)) {
        setShowTokenDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showTokenDropdown]);

  // console.log("UserInfo", userInfo);
  

  return (
    <main className={style.redeem + " pt-5 "}>
      <div className="d-flex justify-content-center">
        <div className={style.container}>
          <div className="mb-5 align-items-center mt-2 d-flex justify-content-between">
            <h1 className='text-center px-3'>Redeem</h1>
            <div className="tooltip-parent position-relative coursor-pointer" style={{ cursor: "pointer" }}>
              <img src="/icons/info.svg" alt="" />
              <p className={"info-tooltip position-absolute"} style={{width:"200px"}}>
                Redeem {currentConfig.xTokenSymbol} for {currentConfig.outputTokenSymbol} and {currentConfig.shieldSymbol} rewards
              </p>
            </div>
          </div>
          
          <form action="javascript:void(0);">
            <div className={style.select_card}>
              <div className='d-flex gap-3 align-items-center'>
                <div className='d-flex align-items-center gap-2 position-relative'>
                  <div className={style.imgdiv + ' d-flex justify-content-center align-items-center'}>
                    <img src={currentConfig.icon} alt={currentConfig.xTokenSymbol} />
                  </div>
                  
                  <div className={`${style.tokenDropdownContainer} position-relative`}>
                    <button 
                      type="button"
                      className={`${style.tokenSelector} border-0 bg-transparent opacity-100 fs-6 mb-0 px-3 d-flex align-items-center`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTokenDropdown(!showTokenDropdown);
                      }}
                    >
                      {currentConfig.xTokenSymbol}
                      <img 
                        src="/icons/arrow-down.png" 
                        alt="dropdown" 
                        className={`ms-2 ${showTokenDropdown ? style.rotate180 : ''}`}
                        style={{ width: '12px', transition: 'transform 0.3s' }}
                      />
                    </button>
                    
                    {showTokenDropdown && (
                      <div className={style.tokenDropdown} onClick={(e) => e.stopPropagation()}>
                        {Object.entries(TOKEN_CONFIG).map(([key, config]) => (
                          <button
                            key={key}
                            type="button"
                            className={`${style.tokenOption} ${selectedToken === key ? style.active : ''}`}
                            onClick={() => handleTokenSelect(key)}
                          >
                            <img src={config.icon} alt={config.xTokenSymbol} width="20" className="me-2" />
                            {config.xTokenSymbol}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <p className='mb-0'>
                  <input 
                    className={`${style.outline_none} w-100 border-0 bg-transparent fs-3 w-fit-content`} 
                    type="number" 
                    placeholder="0.00" 
                    value={alienXInputValue}
                    onChange={(e) =>
                      handleAlienXInputChange(
                        e,
                        parseFloat?.(poolInfo?._redemptionFee) / 10e3
                      )
                    }
                  />
                </p>
              </div>
              <div className='d-flex mt-2 justify-content-between'>
                <button className='mb-0 btn border-0 bg-transparent p-0' onClick={setMax}>Max</button>
                <p className='mb-0'>
                  <span className='me-2'>Balance</span>
                  {alienXBalance?.formatted
                    ? parseFloat?.(alienXBalance?.formatted).toFixed(4)
                    : "0.0"}
                </p>
              </div>
            </div>

            <div className={style.arrowdiv + " my-4 d-flex justify-content-center"}>
              <button type="button" className='d-flex justify-content-center align-items-center bg-transparent border-0'>
                <img src="/icons/arrow-down.png" alt="" />
              </button>
            </div>

            <div className={style.select_card}>
              <div className='d-flex align-items-center gap-3'>
                <div className='d-flex align-items-center gap-2'>
                  <div className={style.imgdiv + ' d-flex justify-content-center align-items-center'}>
                    <img src={currentConfig.icon} alt={currentConfig.outputTokenSymbol} />
                  </div>
                  <p className="opacity-100 fs-6 mb-0 px-3">{currentConfig.outputTokenSymbol}</p>
                </div>
                <p className='mb-0'>
                  <input 
                    className={`${style.outline_none} w-100 border-0 bg-transparent fs-3 w-fit-content`} 
                    type="number"
                    placeholder="0.00"
                    value={alienInputValue === 0 ? "0.0" : alienInputValue}
                    readOnly
                  />
                </p>
              </div>
            </div>

            <div className={style.redeem_plus + " redeem-plus d-flex justify-content-center"}>
              <h4 className="d-flex justify-content-center align-items-center">
                <span>+</span>
              </h4>
            </div>

            <div className={style.select_card}>
              <div className='d-flex align-items-center gap-3'>
                <div className='d-flex align-items-center gap-2'>
                  <div className={style.imgdiv + ' d-flex justify-content-center align-items-center'}>
                    <img src={currentConfig.shieldIcon} alt={currentConfig.shieldSymbol} />
                  </div>
                  <p className="opacity-100 fs-6 mb-0 px-3">{currentConfig.shieldSymbol}</p>
                </div>
                <p className='mb-0'>
                  <input 
                    className={`${style.outline_none} w-100 border-0 bg-transparent fs-3 w-fit-content`} 
                    type="number"
                    placeholder="0.00"
                    value={shieldInputValue === 0 ? "0.0" : shieldInputValue}
                    readOnly
                  />
                </p>
              </div>
            </div>

            <div className={style.balancediv + " mt-3 mb-5"}>
              <div className='d-flex my-1 justify-content-between'>
                <span>Redeem Fee</span>
                <p className="text-end">
                  {parseInt?.(poolInfo?.[3]) > 0
                    ? parseInt?.(poolInfo?.[3]) / 10e3
                    : 0}%
                </p>
              </div>
              <div className='d-flex my-1 justify-content-between'>
                <span>Minimum Received</span>
                <p className="text-end">
                  {alienXInputValue === ""
                    ? "0.0"
                    : parseFloat?.(parseFloat?.(alienInputValue) - parseFloat?.(alienInputValue) * (SlippageInPercent / 100)).toFixed(4)}{" "}
                  {currentConfig.outputTokenSymbol}
                </p>
              </div>
              <div className='d-flex my-1 justify-content-between'>
                <span>Slippage</span>
                <div className='d-flex gap-2'>
                  <button 
                    className={`${style.slippageBtn} ${SlippageInPercent === 0.5 ? style.activeSlippage : ''}`}
                    onClick={() => setSlippageInPercent(0.5)}
                  >
                    0.5%
                  </button>
                  <button 
                    className={`${style.slippageBtn} ${SlippageInPercent === 1 ? style.activeSlippage : ''}`}
                    onClick={() => setSlippageInPercent(1)}
                  >
                    1%
                  </button>
                  <button 
                    className={`${style.slippageBtn} ${SlippageInPercent === 2 ? style.activeSlippage : ''}`}
                    onClick={() => setSlippageInPercent(2)}
                  >
                    2%
                  </button>
                  <input
                    className={`${style.slippageInput} ${SlippageInPercent !== 0.5 && SlippageInPercent !== 1 && SlippageInPercent !== 2 ? style.activeSlippage : ''}`}
                    value={SlippageInPercent}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/[^0-9.]/g, "");
                      if (numericValue === "" || (parseFloat(numericValue) >= 0 && parseFloat(numericValue) <= 100)) {
                        setSlippageInPercent(numericValue === "" ? "" : parseFloat(numericValue));
                      }
                    }}
                  />
                  <span className={style.percentSymbol}>%</span>
                </div>
              </div>
            </div>

            {address && isConnected ? (
              !isAproveERC20 ? (
                <div>
                  <ApproveButton
                    tokenAddress={xTokenAddress}
                    spenderAddress={currentConfig.poolAddress}
                    setIsApprovedERC20={setIsApprovedERC20}
                    color={"white"}
                    isLpToken={false}
                    userInput={alienXInputValue === "" ? "0" : alienXInputValue}
                  />
                </div>
              ) : parseFloat?.(alienXInputValue) > parseFloat?.(alienXBalance?.formatted) ? (
                <div className={style.insufficientFunds}>
                  <button className="btn1 py-3 w-100">
                    Insufficient Funds
                  </button>
                </div>
              ) : (
                <>
                  <button
                    disabled={parseFloat?.(alienXInputValue) > 0 && !isLoadingRedeem ? false : true}
                    onClick={handleRedeem}
                    className={`btn1 py-3 w-100`}
                  >
                    {isLoadingRedeem ? <Loader color="#FF8C00" /> : null}
                    Redeem {currentConfig.xTokenSymbol}
                  </button>
                  <br /><br />

                  {(parseFloat?.(userInfo?.[1]) > 0 || parseFloat?.(userInfo?.[2]) > 0) && (
                    <button
                      disabled={
                        Number(userInfo?.[3]?.toString()) > Number(currentBlock) + 3 ||
                        ((parseFloat?.(userInfo?.[1]) > 0 || parseFloat?.(userInfo?.[2]) > 0) &&
                        !isLoadingCollect ? false : true)
                      }
                      onClick={executeWriteCollect}
                      className={`btn1 py-3 w-100`}
                    >
                      {isLoadingCollect ? <Loader color="#423A2A" /> : null}
                      Collect{" "}
                      {parseFloat?.(userInfo?.[1]) > 0
                        ? `${parseFloat?.(convertWeiToEther?.(userInfo?.[2]?.toString())).toFixed(4)} ${currentConfig.outputTokenSymbol} & 
                           ${parseFloat?.(convertWeiToEther?.(userInfo?.[1]?.toString())).toFixed(4)} ${currentConfig.shieldSymbol}`
                        : `${parseFloat?.(convertWeiToEther?.(userInfo?.[2]?.toString())).toFixed(4)} ${currentConfig.outputTokenSymbol}`}
                    </button>
                  )}
                </>
              )
            ) : (
              <button
                onClick={openConnectModal}
                className={`${style.connectWalletBtn} py-3 w-100`}
              >
                Connect Wallet
              </button>
            )}
          </form>
        </div>
      </div>
      <NewFooter />
    </main>
  );
};

export default Redeem;